import type { Mission } from "@/campaigns/modules/missions/types";
import type { MissionTaskVariant } from "./mission-task-row";

/**
 * Pick the row variant for each task of an ACTIVE mission.
 *
 * Sequential missions (`keep_order`) mark exactly one task active → it gets the single
 * `highlight`; free-order missions mark several → all render `neutral` (no single spotlight).
 * `keep_order` itself is not on the wire, so the count of active tasks is the signal. Before
 * the per-player stream lands nothing is flagged → all neutral.
 *
 * Extracted from `mission-card.tsx` so the sprint-mission task list uses the identical rule
 * (#3867). A hand-rolled variant there missed `"upcoming"` entirely and spotlighted every active
 * task, which diverged visibly from the standard ladder for a free-order mission — the same
 * class of drift `assembleMissionRows` was extracted to prevent on the data side.
 */
export const taskVariant = (
  task: Mission["tasks"][number],
  activeCount: number,
  hasLiveState: boolean
): MissionTaskVariant => {
  if (!hasLiveState) return "neutral";
  if (task.isCompleted) return "done";
  if (task.isActive) return activeCount > 1 ? "neutral" : "highlight";
  return "upcoming";
};

/** The two signals `taskVariant` needs, derived from a mission's task list. */
export const taskVariantInputs = (tasks: Mission["tasks"]) => ({
  activeCount: tasks.filter((task) => task.isActive).length,
  hasLiveState: tasks.some((task) => task.isActive || task.isCompleted),
});
